import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { commifyNumber } from '../../../../helpers/common';
import GradientLine from '../../../../new-components/ui/GradientLine';
import Logo from '../../../../new-components/ui/Logo';
import { EntityTypes } from '../../../../new-lib/constants/common';
import CardTags from '../../../../new-components/cards/CardTags';
import router from 'next/router';
import CTA from '../../../../new-components/ui/CTA';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import Link from 'next/link';
import CompoundPricesSummary from './CompoundPricesSummary';
import RemoveFilterNote from '../../../../new-components/RemoveFilterNote';
import { ExtraContactParams } from '../../../../new-lib/analytics';

interface ChildProps {
  entity: any;
  entityType: EntityTypes;
  contactEventExtraParams?: ExtraContactParams;
}

const StickyDetails = ({
  entity,
  entityType,
  contactEventExtraParams
}: ChildProps): JSX.Element => {
  const { t } = useTranslation(['entity', 'common']);

  const getLogoPath = () => {
    switch (entityType) {
      case EntityTypes.PROPERTY:
        return entity.compound.developer.logo_path;
      case EntityTypes.SUPER_COMPOUND:
      case EntityTypes.COMPOUND:
        return entity.developerLogoUrl;
      case EntityTypes.AREA:
        return entity.image.url;
      case EntityTypes.DEVELOPER:
        return entity.logo_path;
      default:
        return '';
    }
  };
  const getTags = () => {
    if (
      entityType == EntityTypes.PROPERTY &&
      (entity.is_launch_summary || (entity.tags && entity.tags.length > 0))
    ) {
      return (
        <TagsContainer>
          <CardTags
            tags={
              entity.is_launch_summary
                ? ['Launch']
                : (entity.tags as Array<string>)
            }
            isCustom={true}
            withMargin={true}
          />
        </TagsContainer>
      );
    }

    return (
      <span className="text-2" data-test="entity-type">
        {t(
          entityType == EntityTypes.AREA && entity.is_super
            ? EntityTypes.SUPER_AREA
            : entityType,
          { ns: 'common' }
        )}
      </span>
    );
  };

  const getEntityLocation = () => {
    if (entityType == EntityTypes.AREA || entityType == EntityTypes.DEVELOPER)
      return null;
    if (entityType == 'property') {
      return (
        <h2 className="entity-location" itemProp="address">
          <span className="text-2">
            {`${entity.compound.name}, ${entity.compound.area.name}, ${t(
              'egypt',
              { ns: 'common' }
            )}`}
          </span>
        </h2>
      );
    }
    return <span className="entity-location">{null}</span>;
  };

  const getEntityPropertiesCount = () => {
    if (entityType == EntityTypes.AREA)
      return (
        <h2 className="properties-availability">
          {entity.properties_count + ' ' + t('properties_available')}
        </h2>
      );
    else if (entityType == EntityTypes.DEVELOPER)
      return (
        <h2 className="properties-availability">
          {entity.no_of_properties + ' ' + t('properties_available')}
        </h2>
      );
  };

  const getEntityPrices = () => {
    if (entityType == EntityTypes.AREA) return null;

    if (entity.min_price == 0 && entity.max_price == 0 && entity.on_sale) {
      return <h3 className="headline-3 price">{t('price_tba')}</h3>;
    }

    return entity.min_price || entity.max_price ? (
      <h3 className="property-price-section">
        {entity.max_price != entity.min_price ? (
          <p className="price-start">{t('price_start')} </p>
        ) : (
          <p className="price-start">{t('price')}</p>
        )}
        <div className="property-price-details">
          {entity.min_price ? (
            <div className="price-data">
              <p className="headline-1 price">
                {commifyNumber(entity.min_price)} {entity.currency || t('egp')}{' '}
              </p>
            </div>
          ) : (
            <></>
          )}
          {entity.max_price &&
          entity.max_price != entity.min_price &&
          entityType == EntityTypes.PROPERTY ? (
            <div className="price-data max-price">
              <p className="text-2">{t('max_price') + ': '}</p>
              <p className="headline-1 price">
                {commifyNumber(entity.max_price)} {entity.currency || 'EGP'}
              </p>
            </div>
          ) : (
            <></>
          )}
        </div>
      </h3>
    ) : (
      <></>
    );
  };

  const propertyNavigator = () => {
    router.push({
      pathname: `/search`,
      query: {
        [SearchFiltersTypes.PROPERTY_TYPES]: entity.property_type.id,
        [SearchFiltersTypes.BEDROOMS]: entity.number_of_bedrooms,
        [SearchFiltersTypes.MIN_PRICE]: entity.min_price
      }
    });
  };

  return (
    <Container className={entityType} id="entity-data">
      <div className="data-container">
        {entityType === EntityTypes.COMPOUND ||
        entityType === EntityTypes.SUPER_COMPOUND ||
        entityType === EntityTypes.PROPERTY ? (
          <Link
            className="logo-container"
            href={`/developer/${
              entity?.developerId || entity?.compound?.developer_id
              // entity?.developer_id || entity?.compound?.developer_id
            }`}
          >
            <Logo logoPath={getLogoPath()} />
          </Link>
        ) : (
          <div className="logo-container">
            <Logo logoPath={getLogoPath()} />
          </div>
        )}
        <EntityData className={entityType}>
          <div className="entity-name" itemProp="name">
            <h1 className="headline-2">
              {entity.display_name || entity.displayName || entity.name}
            </h1>
            {getTags()}
          </div>
          {getEntityLocation()}
          {getEntityPropertiesCount()}
          <div className="lower-section">
            {entityType == EntityTypes.COMPOUND ||
            entityType == EntityTypes.SUPER_COMPOUND ? (
              <CompoundPricesSummary entity={entity} />
            ) : (
              getEntityPrices()
            )}
            {!entity.on_sale &&
              !entity.is_launch_summary &&
              entityType == EntityTypes.PROPERTY && (
                <SoldOutBtn>{t('sold_out')}</SoldOutBtn>
              )}
            {!entity.on_sale &&
            !entity.is_launch_summary &&
            entityType == EntityTypes.PROPERTY ? (
              <button className="check-property" onClick={propertyNavigator}>
                {t('check_same_property')}
              </button>
            ) : entityType != EntityTypes.AREA ? (
              <div className="action-btns">
                <CTA
                  entity={entity}
                  entityType={entityType}
                  eventExtraParams={contactEventExtraParams}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
          {(entityType == EntityTypes.COMPOUND ||
            entityType == EntityTypes.SUPER_COMPOUND) && <RemoveFilterNote />}
        </EntityData>
        {entityType == EntityTypes.AREA ? (
          <div className="action-btns-area">
            <CTA entity={entity} entityType={entityType} />
          </div>
        ) : (
          <></>
        )}
      </div>

      <GradientLine height={'1px'} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  background-color: ${props => props.theme.light.backgroundColor};
  position: sticky;
  top: 70px;
  padding-top: 16px;
  z-index: 11;
  .data-container {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 20px;
    .logo-container {
      margin-inline-end: 25px;
      width: 90px;
      height: 90px;
      .logo-wrapper:hover {
        @media (hover: hover) and (pointer: fine) {
          background: #e6e6e6;
        }
      }
    }
  }

  .properties-availability {
    font-size: ${props => props.theme.font.sizes.s};
    color: ${props => props.theme.light.colors.black};
    padding-bottom: 4px;
  }

  .action-btns-area {
    height: 90px;
    display: flex;
    flex-direction: column;
    margin-inline-start: auto;
    justify-content: flex-end;
    min-width: 250px;
  }

  .action-btns-area > div {
    margin-top: auto;

    a {
      margin-inline-start: auto;
    }
  }
`;

const EntityData = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  .entity-name {
    width: 100%;
    display: flex;
    flex-direction: row;
    margin-bottom: 2.5px;
    h2 {
      display: flex;
      align-items: center;
      font-size: 20px;
      text-transform: capitalize;
    }
    span {
      color: ${props => props.theme.light.colors.black};
      margin-inline-start: 16px;
      border-radius: ${props => props.theme.borderRadius};
      padding: 0 16px;
      font-family: materialMedium;
      background-color: #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
      max-height: 30px;
    }
  }

  .entity-location {
    display: flex;
    flex-direction: row;
    margin-bottom: 4px;
    align-items: center;
    span {
      color: #646464;
    }
  }

  .lower-section {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .property-price-section {
      .price-start {
        font-size: 10px;
        font-family: materialRegular;
        color: #b6b6b6;
      }

      .property-price-details {
        margin-top: -2px;
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: 8px;
        .price-data {
          margin-inline-end: 5px;
          display: flex;
          gap: 4px;
          flex-direction: row;
          align-items: center;

          p {
            color: ${props => props.theme.light.colors.black};
          }
          h2 {
            color: ${props => props.theme.light.colors.black};
          }
          h3 {
            color: ${props => props.theme.light.colors.black};
            font-size: 24px;
          }
          &.max-price {
            padding-inline-start: 8px;

            h3 {
              font-family: materialRegular;
              color: ${props => props.theme.light.colors.darkGrey};
              font-size: ${props => props.theme.font.sizes.s};
              margin-inline-start: 8px;
            }
          }
        }
      }
    }

    .price-section {
      display: flex;
      gap: 8px;

      p.price {
        color: ${props => props.theme.light.colors.black};
        font-size: 21px;
        font-family: materialBold;
        line-height: 150%;
      }
      p.currency {
        color: #697586;
        font-family: materialMedium;
        font-size: 11px;
      }

      .price-container {
        width: 294px;
        display: flex;
        flex-direction: column;
        height: 100%;

        p.price-type {
          font-size: 17px;
          font-family: materialMedium;
          color: #697586;
          line-height: 150%;
        }

        .price-details {
          display: flex;
          align-items: center;
          gap: 8px;
          p.price {
            color: ${props => props.theme.light.colors.black};
            font-size: 21px;
            font-family: materialBold;
            line-height: 150%;
          }
          p.currency {
            color: #697586;
            font-family: materialMedium;
            font-size: 11px;
          }
        }
      }
      .separator {
        height: auto;
        width: 1px;
        background-color: #d1d4d9;
      }
    }

    .action-btns {
      margin-inline-start: auto;
    }

    .check-property {
      margin-inline-start: auto;
      padding: 5px 16px;
      background: ${props => props.theme.light.colors.whitish};
      border-radius: ${props => props.theme.borderRadius};
      border: solid 1px ${props => props.theme.light.colors.whitish};
      cursor: pointer;
      font-size: ${props => props.theme.font.sizes.s};
      color: ${props => props.theme.light.colors.primaryBlue};
      font-family: materialMedium;
      &:hover {
        background-color: ${props => props.theme.light.colors.primaryBlue};
        color: ${props => props.theme.light.colors.white};
      }
    }
  }
  .filter-note {
    width: 490px;
    margin-top: 8px;
  }
`;
const TagsContainer = styled.div`
  display: flex;
  width: auto;
  position: relative;
`;
const SoldOutBtn = styled.div`
  padding: 2.5px 16px;
  background: #c1272d;
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.font.sizes.s};
  color: ${props => props.theme.light.colors.white};
`;

export default StickyDetails;
